import type { PointerEvent } from 'react';
import { Eye, GripVertical, Pencil, Power, Trash2 } from 'lucide-react';
import { formatDate } from '@/shared/lib/format';
import type { Banner } from '@/shared/types/banner';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/ui/dropdown-menu';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { SelectableCard } from '@/shared/components/selectable-card';
import { resolveBannerSrc } from '../model/banner-image';

interface BannerCardProps {
  banner: Banner;
  selected: boolean;
  onToggle: () => void;
  index: number;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  /** Quando false, desabilita ações que dependem de `_id` persistido no Mongo. */
  canPersist?: boolean;
  /** Quando fornecido, exibe o drag handle e chama este handler no pointerDown. */
  onDragHandlePointerDown?: (e: PointerEvent<HTMLButtonElement>) => void;
}

export function BannerCard({
  banner,
  selected,
  onToggle,
  index,
  onPreview,
  onEdit,
  onDelete,
  onToggleActive,
  canPersist = true,
  onDragHandlePointerDown,
}: BannerCardProps) {
  const desc = banner.description ?? '';
  const shortDesc = desc.length > 56 ? `${desc.slice(0, 56)}…` : desc;

  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      index={index}
      ariaLabel={`Selecionar banner ${banner.title ?? ''}`}
      onCardClick={canPersist ? onEdit : undefined}
      actions={
        <div className="flex items-center gap-1">
          {onDragHandlePointerDown ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Arrastar para reordenar"
              onPointerDown={onDragHandlePointerDown}
              onClick={(e) => e.stopPropagation()}
              className="size-10 cursor-grab touch-none rounded-lg border-border/60 bg-background/60 shadow-sm active:cursor-grabbing"
            >
              <GripVertical className="size-4 text-muted-foreground" />
            </Button>
          ) : null}
          <ListCardActions
            menuChildren={
              <>
                <DropdownMenuItem onSelect={onPreview}>
                  <Eye />
                  Visualizar
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onEdit} disabled={!canPersist}>
                  <Pencil />
                  Editar
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onToggleActive} disabled={!canPersist}>
                  <Power />
                  {banner.active ? 'Desativar' : 'Ativar'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onDelete} disabled={!canPersist} destructive>
                  <Trash2 />
                  Excluir
                </DropdownMenuItem>
              </>
            }
          />
        </div>
      }
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="size-12 shrink-0 overflow-hidden rounded-lg border border-border/50 bg-muted/30 sm:size-14">
          <img src={resolveBannerSrc(banner.banner)} alt="" className="size-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-semibold sm:text-base">{banner.title || 'Sem título'}</p>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              #{banner.order ?? index + 1}
            </span>
          </div>
          {shortDesc ? (
            <p className="mt-0.5 truncate text-xs text-muted-foreground sm:text-sm">{shortDesc}</p>
          ) : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant={banner.active ? 'success' : 'secondary'} className="h-6 font-normal">
              {banner.active ? 'Ativo' : 'Inativo'}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatDate(banner.entryDate, '—')} → {formatDate(banner.exitDate, 'Sem expiração')}
            </span>
            {!canPersist ? (
              <Badge variant="secondary" className="h-6 font-normal text-amber-700">
                Sem id
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    </SelectableCard>
  );
}
